import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PageHeader } from "@/components/PageHeader";
import { TmdbAttribution } from "@/components/Brand";

const Section = ({ title, children }) => (
  <section className="rounded-3xl border bg-card p-6 space-y-4">
    <h2 className="font-display text-lg font-bold">{title}</h2>
    {children}
  </section>
);

function StatGrid({ stats }) {
  const items = [["Swipes", stats.swipes], ["Watchlist", stats.watchlist], ["Liked", stats.liked], ["Didn't like", stats.disliked]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map(([k, v]) => (
        <div key={k} className="rounded-2xl bg-secondary p-4" data-testid={`stat-${k.toLowerCase().replace(/[^a-z]/g, "")}`}>
          <p className="font-display text-2xl font-black">{v}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{k}</p>
        </div>
      ))}
    </div>
  );
}

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [name, setName] = useState(user.name);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/me/stats").then(({ data }) => setStats(data)); }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/me", { name });
      setUser(data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <PageHeader eyebrow="Account" title="Settings" />
      {stats && <StatGrid stats={stats} />}
      {user.is_guest ? (
        <Section title="Guest">
          <p className="text-sm text-muted-foreground" data-testid="settings-guest-note">
            You're using StreamPick without an account. Everything is kept in this browser only and could be lost if it's cleared or you switch devices.
          </p>
          <Button asChild className="rounded-full h-11 px-6 font-bold bg-amber-400 text-black hover:bg-amber-300" data-testid="settings-save-progress-button">
            <Link to="/login">Save progress / Log in</Link>
          </Button>
        </Section>
      ) : (
      <Section title="Profile">
        <div className="space-y-2">
          <Label htmlFor="name">Display name</Label>
          <div className="flex gap-2">
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" data-testid="settings-name-input" />
            <Button onClick={save} className="rounded-full h-11 px-6" data-testid="settings-save-button">Save</Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="settings-email">{user.email} · signed in with {user.auth_provider === "google" ? "Google" : "email"}</p>
      </Section>
      )}
      <Section title="Appearance">
        <div className="flex items-center justify-between">
          <Label htmlFor="dark">Dark mode</Label>
          <Switch id="dark" checked={theme === "dark"} onCheckedChange={(c) => setTheme(c ? "dark" : "light")} data-testid="settings-theme-switch" />
        </div>
      </Section>
      <Section title="Taste">
        <Link to="/genres" className="text-primary font-semibold" data-testid="settings-genres-link">Revisit genre preferences →</Link>
      </Section>
      {!user.is_guest && (
        <Button variant="outline" className="rounded-full" data-testid="settings-logout-button"
          onClick={async () => { await logout(); navigate("/login", { replace: true }); }}>Sign out</Button>
      )}
      <TmdbAttribution />
    </div>
  );
}
